/**
 * 現場の作業ビュー（キオスク） — 2026-11-05。
 * 承認済みの「今の版」の手順を大きく見せ、品質チェックと結果の記録までを1画面で行う。
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { dmsApi } from '@/pages/docs/dmsApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mfg`;
const NG_REASONS = ['反り', '剥がれ', '寸法不良', '傷', 'その他'];

const fileUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/dms/documents/${id}/file`;

const judgeBadge = (j) => (j === 'pass'
  ? ['合格', 'bg-emerald-100 text-emerald-800 border-emerald-300']
  : j === 'out_of_spec' || j === 'fail'
    ? ['基準外', 'bg-rose-100 text-rose-800 border-rose-300']
    : ['未入力', 'bg-zinc-100 text-zinc-600 border-zinc-300']);

export const WorkInstructionViewer = ({ wo, operator, open, onOpenChange, onChanged }) => {
  const [state, setState] = useState(null);
  const [idx, setIdx] = useState(0);
  const [qc, setQc] = useState({});
  const [run, setRun] = useState({ ok: 0, ng: 0, reason: '', material: '', doc: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!wo) return;
    const r = await axios.get(`${API}/work-orders/${wo.id}/qc`);
    setState(r.data);
  }, [wo]);

  useEffect(() => {
    if (!open) return;
    setIdx(0); setQc({}); setRun({ ok: 0, ng: 0, reason: '', material: '', doc: '' });
    load().catch(() => setState({ instruction: null, due: [], records: [] }));
  }, [open, load]);

  if (!wo) return null;
  const inst = state?.instruction;
  const steps = inst?.steps || [];
  const total = steps.length + 2;            // 手順 + 品質チェック + 結果を記録
  const step = steps[idx];

  const upload = async (file, onDone) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', `${wo.wo_no || wo.manufacturing_no} 現場写真`);
      fd.append('doc_type', 'photo');
      const r = await dmsApi.uploadLinked('mfg_work_order', wo.id, fd);
      onDone(r?.document?.id || '');
      toast.success('写真を登録しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '写真を登録できませんでした');
    } finally { setBusy(false); }
  };

  const saveQc = async () => {
    const results = (state.due || []).map((c) => ({
      check_id: c.id,
      value: qc[c.id]?.value ?? null,
      judgement: qc[c.id]?.judgement ?? null,
      document_id: qc[c.id]?.document_id ?? null,
    }));
    if (!results.length) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/work-orders/${wo.id}/qc`, {
        operator_name: operator?.name || '現場', operator_id: operator?.id || null, results });
      setState(r.data); setQc({});
      toast.success('品質チェックを記録しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '記録できませんでした');
    } finally { setBusy(false); }
  };

  const saveRun = async () => {
    if (run.ok + run.ng <= 0) return toast.error('OK数とNG数の合計を1以上にしてください');
    if (run.ng > 0 && !run.reason) return toast.error('NGがあるためNG理由を選んでください');
    if (run.ng > 0 && !run.doc) return toast.error('NGがあるため写真が必要です');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/work-orders/${wo.id}/runs`, {
        ok_qty: run.ok, ng_qty: run.ng, ng_reason: run.reason || null,
        material_qty: run.material === '' ? null : Number(run.material),
        document_id: run.doc || null,
        operator_name: operator?.name || '現場', operator_id: operator?.id || null });
      setState(r.data);
      setRun({ ok: 0, ng: 0, reason: '', material: '', doc: '' });
      toast.success('記録しました');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '記録できませんでした');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[92vh] max-w-[96vw] bg-white p-0" aria-describedby={undefined}
        data-testid="work-viewer">
        <div className="flex h-full min-h-0">
          <aside className="w-64 shrink-0 overflow-y-auto border-r border-zinc-200 bg-zinc-50 p-3">
            <p className="text-base font-bold text-zinc-900">{wo.product_name}</p>
            <p className="text-sm text-zinc-500">{wo.wo_no || wo.manufacturing_no} ・ {wo.qty}{wo.product_uom || ''}</p>
            {inst ? (
              <p className="mt-2 text-sm font-semibold text-emerald-700" data-testid="work-viewer-rev">
                作業手順書 rev {inst.rev} ・ 承認 {(inst.approved_at || '').slice(5, 10).replace('-', '/')}
              </p>
            ) : (
              <p className="mt-2 rounded-md bg-amber-100 px-2 py-1 text-sm font-bold text-amber-900"
                data-testid="work-viewer-no-instruction">手順書なし</p>
            )}
            <ul className="mt-3 space-y-1">
              {steps.map((s, i) => (
                <li key={s.id}>
                  <button onClick={() => setIdx(i)} data-testid={`work-viewer-step-${i}`}
                    className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-base ${
                      i === idx ? 'bg-blue-600 text-white' : 'hover:bg-zinc-200'}`}>
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${
                      i < idx ? 'bg-emerald-500 text-white' : 'bg-white text-zinc-600 border'}`}>
                      {i < idx ? '✓' : i + 1}
                    </span>
                    <span className="truncate">{s.title}</span>
                  </button>
                </li>
              ))}
              <li>
                <button onClick={() => setIdx(steps.length)} data-testid="work-viewer-step-qc"
                  className={`w-full rounded-lg px-2 py-2 text-left text-base ${
                    idx === steps.length ? 'bg-blue-600 text-white' : 'hover:bg-zinc-200'}`}>品質チェック</button>
              </li>
              <li>
                <button onClick={() => setIdx(steps.length + 1)} data-testid="work-viewer-step-record"
                  className={`w-full rounded-lg px-2 py-2 text-left text-base ${
                    idx === steps.length + 1 ? 'bg-blue-600 text-white' : 'hover:bg-zinc-200'}`}>結果を記録</button>
              </li>
            </ul>
            {(inst?.docs || []).length > 0 && (
              <a href={fileUrl(inst.docs[0].document_id)} target="_blank" rel="noopener noreferrer"
                className="mt-3 block rounded-lg border border-zinc-300 px-2 py-2 text-center text-sm font-semibold text-blue-700"
                data-testid="work-viewer-open-doc">PDF・動画を開く</a>
            )}
          </aside>

          <main className="min-w-0 flex-1 overflow-y-auto p-6">
            {idx < steps.length && step && (
              <div data-testid="work-viewer-body">
                <h2 className="text-2xl font-bold text-zinc-900">{idx + 1}. {step.title}</h2>
                {(step.media || []).map((m) => (
                  <a key={m.document_id} href={fileUrl(m.document_id)} target="_blank" rel="noopener noreferrer"
                    className="mt-4 block" data-testid={`work-viewer-media-${m.document_id}`}>
                    {m.kind === 'photo' ? (
                      <img src={fileUrl(m.document_id)} alt={m.title || step.title}
                        className="max-h-[42vh] rounded-xl border border-zinc-200 object-contain" />
                    ) : (
                      <span className="inline-block rounded-lg border border-zinc-300 px-3 py-2 text-base font-semibold text-blue-700">
                        {m.kind === 'video' ? '動画を開く' : 'PDFを開く'}：{m.title}
                      </span>
                    )}
                  </a>
                ))}
                {step.body && <p className="mt-4 whitespace-pre-wrap text-base leading-7 text-zinc-800">{step.body}</p>}
                {step.point && (
                  <p className="mt-4 rounded-xl border-l-4 border-amber-400 bg-amber-50 p-4 text-base font-semibold text-amber-900"
                    data-testid="work-viewer-point">ポイント：{step.point}</p>
                )}
              </div>
            )}

            {idx === steps.length && (
              <div data-testid="work-viewer-qc">
                <h2 className="text-2xl font-bold text-zinc-900">品質チェック</h2>
                {!inst && <p className="mt-3 text-base text-zinc-600">手順書がないため検査項目はありません。</p>}
                {(state?.due || []).length === 0 && inst && (
                  <p className="mt-3 text-base text-emerald-700" data-testid="work-viewer-qc-done">
                    いま必要なチェックはありません
                  </p>
                )}
                <div className="mt-4 space-y-3">
                  {(state?.due || []).map((c) => {
                    const v = qc[c.id] || {};
                    const judged = c.type === 'numeric' && v.value !== undefined && v.value !== ''
                      ? ((c.lower !== null && Number(v.value) < c.lower)
                        || (c.upper !== null && Number(v.value) > c.upper) ? 'out_of_spec' : 'pass')
                      : v.judgement;
                    const [label, cls] = judgeBadge(judged);
                    return (
                      <div key={c.id} className="rounded-xl border border-zinc-200 p-4"
                        data-testid={`qc-item-${c.id}`}>
                        <p className="text-base font-bold text-zinc-900">
                          {c.name}
                          <span className="ml-2 text-sm font-normal text-zinc-500">
                            {c.type_label} ・ {c.frequency_label}{c.method ? ` ・ ${c.method}` : ''}
                          </span>
                          <span className={`ml-2 rounded-full border px-2 py-0.5 text-xs font-semibold ${cls}`}
                            data-testid={`qc-badge-${c.id}`}>{label}</span>
                        </p>
                        {c.type === 'pass_fail' && (
                          <div className="mt-3 flex gap-3">
                            <Button className="h-14 flex-1 bg-emerald-600 text-lg hover:bg-emerald-700"
                              onClick={() => setQc((p) => ({ ...p, [c.id]: { judgement: 'pass' } }))}
                              data-testid={`qc-pass-${c.id}`}>合格</Button>
                            <Button variant="destructive" className="h-14 flex-1 text-lg"
                              onClick={() => setQc((p) => ({ ...p, [c.id]: { judgement: 'fail' } }))}
                              data-testid={`qc-fail-${c.id}`}>不合格</Button>
                          </div>
                        )}
                        {c.type === 'numeric' && (
                          <div className="mt-3">
                            <p className="text-sm text-zinc-600">
                              基準 {c.target}{c.uom}（{c.lower ?? '—'} 〜 {c.upper ?? '—'}{c.uom}）
                            </p>
                            <Input type="number" className="mt-1 h-14 max-w-[12rem] text-xl"
                              value={v.value ?? ''}
                              onChange={(e) => setQc((p) => ({ ...p, [c.id]: { value: e.target.value } }))}
                              data-testid={`qc-value-${c.id}`} />
                          </div>
                        )}
                        {c.type === 'photo' && (
                          <Input type="file" accept="image/*" capture="environment" className="mt-3"
                            onChange={(e) => upload(e.target.files?.[0],
                              (id) => setQc((p) => ({ ...p, [c.id]: { document_id: id } })))}
                            data-testid={`qc-photo-${c.id}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
                {(state?.due || []).length > 0 && (
                  <Button className="mt-4 h-14 w-full text-lg" onClick={saveQc} disabled={busy}
                    data-testid="qc-save">品質チェックを記録</Button>
                )}
                {(state?.records || []).length > 0 && (
                  <ul className="mt-5 space-y-1 text-sm text-zinc-600" data-testid="qc-records">
                    {state.records.map((r) => (
                      <li key={r.id}>
                        {(r.at || '').slice(5, 16).replace('T', ' ')} ・ rev {r.rev} ・ {r.operator_name} ・
                        {(r.results || []).map((x) => ` ${x.name}:${x.value ?? ''}${judgeBadge(x.judgement)[0]}`).join('')}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {idx === steps.length + 1 && (
              <div data-testid="work-viewer-record">
                <h2 className="text-2xl font-bold text-zinc-900">結果を記録</h2>
                <div className="mt-4 grid max-w-xl grid-cols-2 gap-4">
                  {[['ok', 'OK数'], ['ng', 'NG数']].map(([k, lb]) => (
                    <div key={k}>
                      <p className="text-base font-semibold text-zinc-700">{lb}</p>
                      <div className="mt-1 flex items-center gap-2">
                        <Button variant="outline" className="h-14 w-14 text-2xl"
                          onClick={() => setRun((p) => ({ ...p, [k]: Math.max(0, p[k] - 1) }))}
                          data-testid={`run-${k}-minus`}>−</Button>
                        <span className="w-16 text-center text-3xl font-bold tabular-nums"
                          data-testid={`run-${k}-value`}>{run[k]}</span>
                        <Button variant="outline" className="h-14 w-14 text-2xl"
                          onClick={() => setRun((p) => ({ ...p, [k]: p[k] + 1 }))}
                          data-testid={`run-${k}-plus`}>＋</Button>
                      </div>
                    </div>
                  ))}
                </div>
                {run.ng > 0 && (
                  <div className="mt-4 max-w-xl">
                    <p className="text-base font-semibold text-zinc-700">NG理由（必須）</p>
                    <select value={run.reason} onChange={(e) => setRun((p) => ({ ...p, reason: e.target.value }))}
                      className="mt-1 h-14 w-full rounded-md border border-zinc-300 bg-white px-3 text-lg"
                      data-testid="run-ng-reason">
                      <option value="">選んでください</option>
                      {NG_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <Input type="file" accept="image/*" capture="environment" className="mt-3"
                      onChange={(e) => upload(e.target.files?.[0], (id) => setRun((p) => ({ ...p, doc: id })))}
                      data-testid="run-photo" />
                    {!run.doc && (
                      <p className="mt-1 text-sm font-semibold text-rose-700" data-testid="run-photo-required">
                        NGがあるため写真が必要です
                      </p>
                    )}
                  </div>
                )}
                <div className="mt-4 max-w-xl">
                  <p className="text-base font-semibold text-zinc-700">材料の実績（任意・部品表どおりなら空欄）</p>
                  <Input type="number" className="mt-1 h-14 max-w-[14rem] text-xl" value={run.material}
                    onChange={(e) => setRun((p) => ({ ...p, material: e.target.value }))}
                    data-testid="run-material" />
                </div>
                <Button className="mt-5 h-16 w-full max-w-xl bg-emerald-600 text-xl hover:bg-emerald-700"
                  onClick={saveRun} disabled={busy} data-testid="run-save">記録する</Button>
                {(state?.work_runs || []).filter((r) => !r.undone_at).length > 0 && (
                  <ul className="mt-5 space-y-1 text-sm text-zinc-600" data-testid="run-records">
                    {state.work_runs.filter((r) => !r.undone_at).map((r) => (
                      <li key={r.id}>
                        記録{r.no}: OK {r.ok_qty} / NG {r.ng_qty}
                        {r.ng_reason ? `（${r.ng_reason}）` : ''} ・ {r.operator_name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </main>
        </div>

        <div className="flex items-center justify-between border-t border-zinc-200 px-6 py-3">
          <Button variant="outline" className="h-14 w-32 text-lg" disabled={idx === 0}
            onClick={() => setIdx((i) => Math.max(0, i - 1))} data-testid="work-viewer-prev">前へ</Button>
          <span className="text-base text-zinc-500" data-testid="work-viewer-progress">
            {idx + 1} / {total}
          </span>
          <Button className="h-14 w-32 text-lg" disabled={idx >= total - 1}
            onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
            data-testid="work-viewer-next">次へ</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
